import { create } from "zustand";
import { Socket, io } from "socket.io-client";
import { useAuthStore, UserAuth } from "../store/useAuthStore";

interface SocketState {
  socket: Socket | null;
  isConnected: boolean;
  connectSocket: (userId?:string, isMobile?:boolean) => void;
  disconnectSocket: () => void;
  sendUserConnect: (params: { userId: string; isMobile: boolean }) => void;
}

export const useSocketStore = create<SocketState>((set, get) => {
  let initialized = false;
  let retryCount = 0;
  let heartbeatInterval: NodeJS.Timeout | null = null;
  let cleanupVisibilityListeners: (() => void) | null = null;
  let manualDisconnect = false;
  let lastUserConnectedEmitAt = 0;
  let lastUserConnectedUserId: string | null = null;

  useAuthStore.subscribe(
    (state, prevState) => {
      const { connectSocket, disconnectSocket } = get();
      const newUser = state.user;

      if (newUser && !initialized) {
        connectSocket(newUser.id, true);
        initialized = true;
      }

      if (!newUser && initialized) {
        disconnectSocket();
        initialized = false;
      }
    }
  );

  return {
    socket: null,
    isConnected: false,

    connectSocket: (userId, isMobile) => {
      manualDisconnect = false;
      const { socket, isConnected } = get();

      if (socket && isConnected) {
        if (!useAuthStore.getState().user) return;
        socket.emit("user_connected", { userId: useAuthStore.getState().user?.id, isMobile: isMobile ?? true });
        return;
      }

      // Se ja existe socket mas esta desconectado, destroi e recria
      if (socket && !isConnected) {
        socket.removeAllListeners();
        socket.disconnect();
      }

      const socketInstance: Socket = io(process.env.REACT_APP_API_URL as string, {
        transports: ["websocket"],
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 15000,
        rejectUnauthorized: process.env.NODE_ENV === "production",
        upgrade: false,
      });

      const emitAppVisibility = () => {
        if (!socketInstance.connected) return;
        const state = document.visibilityState === "hidden" ? "hidden" : "visible";
        socketInstance.emit("app:visibility", { state });
      };

      const emitUserConnected = () => {
        const user = useAuthStore.getState().user;
        if (user && socketInstance.connected) {
          const now = Date.now();
          const SAME_USER_DEBOUNCE_MS = 3000;
          if (
            lastUserConnectedUserId === user.id &&
            now - lastUserConnectedEmitAt < SAME_USER_DEBOUNCE_MS
          ) {
            return;
          }
          lastUserConnectedUserId = user.id;
          lastUserConnectedEmitAt = now;
          socketInstance.emit("user_connected", { userId: user.id, isMobile: isMobile ?? true });
        }
      };

      socketInstance.on("connect", () => {
        console.log("[SocketStore] Socket conectado");
        set({ isConnected: true });
        retryCount = 0;
        emitUserConnected();
        emitAppVisibility();

        if (heartbeatInterval) clearInterval(heartbeatInterval);
        heartbeatInterval = setInterval(() => {
          if (socketInstance.connected) {
            socketInstance.emit("ping");
          } else {
            if (heartbeatInterval) {
              clearInterval(heartbeatInterval);
              heartbeatInterval = null;
            }
          }
        }, 25000);
      });

      socketInstance.on("disconnect", (reason) => {
        console.warn("[SocketStore] Socket desconectado:", reason);
        set({ isConnected: false });

        if (heartbeatInterval) {
          clearInterval(heartbeatInterval);
          heartbeatInterval = null;
        }

        // Se o servidor fechou a conexao ou transporte quebrou, forca reconexao
        if (!manualDisconnect && (reason === "transport close" || reason === "transport error" || reason === "ping timeout")) {
          console.log("[SocketStore] Forçando reconexão após disconnect:", reason);
          setTimeout(() => {
            if (!socketInstance.connected && !manualDisconnect) {
              socketInstance.connect();
            }
          }, 1000);
        }
      });

      socketInstance.on("connect_error", (error) => {
        console.error("[SocketStore] Erro de conexão:", error.message);
        set({ isConnected: false });
        retryCount++;
      });

      socketInstance.on("reconnect", (attemptNumber) => {
        console.log(`[SocketStore] Reconectado após ${attemptNumber} tentativas`);
        retryCount = 0;
        emitUserConnected();
        emitAppVisibility();
      });

      socketInstance.on("pong", () => {});

      // Reconexao forcada quando o app volta do background
      if (cleanupVisibilityListeners) {
        cleanupVisibilityListeners();
      }

      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible" && !manualDisconnect) {
          const user = useAuthStore.getState().user;
          if (!user) return;

          if (!socketInstance.connected) {
            console.log("[SocketStore] App voltou ao foreground, socket morto. Reconectando...");
            socketInstance.connect();
          } else {
            emitUserConnected();
            emitAppVisibility();
          }
        } else {
          emitAppVisibility();
        }
      };

      const handleOnline = () => {
        if (!socketInstance.connected && !manualDisconnect && useAuthStore.getState().user) {
          console.log("[SocketStore] Rede voltou. Reconectando socket...");
          socketInstance.connect();
        }
      };

      // iOS standalone PWA: focus fires mais confiavelmente que visibilitychange em alguns edge cases
      const handleFocus = () => {
        if (manualDisconnect || !useAuthStore.getState().user) return;
        if (!socketInstance.connected) {
          console.log("[SocketStore] Focus recebido, socket morto. Reconectando...");
          socketInstance.connect();
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);
      window.addEventListener("pageshow", handleVisibilityChange);
      window.addEventListener("online", handleOnline);
      window.addEventListener("focus", handleFocus);

      cleanupVisibilityListeners = () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        window.removeEventListener("pageshow", handleVisibilityChange);
        window.removeEventListener("online", handleOnline);
        window.removeEventListener("focus", handleFocus);
      };

      set({ socket: socketInstance });
      socketInstance.connect();
    },

    disconnectSocket: () => {
      const { socket } = get();
      manualDisconnect = true;
      if (socket) {
        socket.removeAllListeners();
        socket.disconnect();
        set({ socket: null, isConnected: false });
        console.log("[SocketStore] Socket desconectado manualmente");

        if (heartbeatInterval) {
          clearInterval(heartbeatInterval);
          heartbeatInterval = null;
        }
        if (cleanupVisibilityListeners) {
          cleanupVisibilityListeners();
          cleanupVisibilityListeners = null;
        }
      }
    },

    sendUserConnect: (params: { userId: string; isMobile: boolean }) => {
      const { socket } = get();
      if (socket?.connected) {
        socket.emit("user_connected", params);
      }
    }
  };
});